import { HttpErrorResponse } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import {
  AuthHeaders,
  BaseApiService,
  CoreInjectorTokens,
  Environment,
  GetManyDto,
  PageableListDto,
  PageableListV2Dto,
  ResponseDto,
  TenantDto,
} from '@fleet-beat/core';
import { catchError, map, Observable } from 'rxjs';
import { JsonPatchOperationModel } from '../../models';

@Injectable({
  providedIn: 'root',
})
export class TenantsApiService {
  private readonly baseUrl: string = `${this.environment.tenantsApiUrl}/api/tenants`;
  private readonly baseAdminUrl: string = `${this.environment.apiEndpoint}/admin/tenants`;

  constructor(
    @Inject(CoreInjectorTokens.Environment) private environment: Environment,
    private baseApiService: BaseApiService,
  ) {}

  getUserTenants(auth: AuthHeaders): Observable<TenantDto[]> {
    return this.baseApiService.get(`${this.baseUrl}/getUserTenants`, { auth }).pipe(
      catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
      map((response: ResponseDto<TenantDto[]>) => this.baseApiService.mapResponse(response)),
    );
  }

  getTenants(options: GetManyDto, auth: AuthHeaders): Observable<PageableListDto<TenantDto>> {
    return this.baseApiService.get(`${this.baseUrl}`, { auth, params: options }).pipe(
      catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
      map((response: ResponseDto<PageableListDto<TenantDto>>) => this.baseApiService.mapResponse(response)),
    );
  }
  getManyAsAdmin(params: GetManyDto, auth: AuthHeaders): Observable<PageableListV2Dto<TenantDto>> {
    return this.baseApiService.get(`${this.baseAdminUrl}`, { auth, params }).pipe(
      catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
      map((response: ResponseDto<TenantDto[]>) => this.baseApiService.mapResponseV2(response)),
      map((response: ResponseDto<TenantDto[]>) => {
        return {
          data: response.data,
          ...response.meta,
        };
      }),
    );
  }
  updateAsAdminById(tenantId: string, data: TenantDto, auth: AuthHeaders): Observable<ResponseDto<TenantDto>> {
    return this.baseApiService.patch(`${this.baseAdminUrl}/${tenantId}`, { ...data }, { auth }).pipe(
      catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
      map((response: ResponseDto<TenantDto>) => this.baseApiService.mapResponseV2(response)),
      map((response: ResponseDto<TenantDto>) => {
        return {
          data: response.data,
          ...response.meta,
        };
      }),
    );
  }
  createAsAdmin(data: TenantDto, auth: AuthHeaders): Observable<ResponseDto<TenantDto>> {
    return this.baseApiService.post(`${this.baseAdminUrl}`, data, { auth }).pipe(
      catchError((error: HttpErrorResponse) => {
        this.baseApiService.catchApiError(error);
        alert(error);
      }),
      map((response: ResponseDto<TenantDto>) => this.baseApiService.mapResponseV2(response)),
      map((response: ResponseDto<TenantDto>) => {
        return {
          data: response.data,
          ...response.meta,
        };
      }),
    );
  }
  updateTenant(auth: AuthHeaders, payload: JsonPatchOperationModel[], id: string): Observable<TenantDto> {
    return this.baseApiService.put(`${this.baseUrl}/${id}`, payload, { auth }).pipe(
      catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
      map((response: ResponseDto<TenantDto>) => this.baseApiService.mapResponse(response)),
    );
  }
}
